import React, { memo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import joypixels from 'emoji-toolkit';

import { Tooltip } from '@mic3/platform-ui';
import Flex from 'app/components/atoms/Flex/Flex';

const stripColons = emoji => (emoji || '').replace(/^:|:$/g, '');

const ALLOWED_EMOJI_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif']);
const safeEmojiExtension = (ext) => {
    const normalized = String(ext || '').toLowerCase();
    return ALLOWED_EMOJI_EXTENSIONS.has(normalized) ? normalized : 'png';
};

const resolveUnicode = shortcode => {
    if (!shortcode) return null;
    const normalized = shortcode.startsWith(':') && shortcode.endsWith(':') ? shortcode : `:${shortcode}:`;
    const out = joypixels.shortnameToUnicode(normalized);
    return out && out !== normalized ? out : null;
};

const ReactionsRow = styled(Flex)`
    flex-wrap: wrap;
    gap: 4px;
    margin: 6px 0 0;
`;

const Chip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    font-size: 12px;
    line-height: 1.2;
    border: 1px solid ${({ theme, active }) => (active ? `${theme.material.colors.primary.main}66` : theme.material.colors.background.divider)};
    background: ${({ theme, active }) => (active ? `${theme.material.colors.primary.main}33` : 'transparent')};
    color: ${({ theme }) => theme.material.colors.text.primary};
    &:hover {
        border-color: ${({ theme, disabled }) => (disabled ? theme.material.colors.background.divider : theme.material.colors.primary.main)};
    }
`;

const Glyph = styled.span`
    font-size: 16px;
    line-height: 1;
`;

const FallbackText = styled.span`
    font-size: 11px;
    opacity: 0.85;
`;

const CustomEmojiImg = styled.img`
    width: 16px;
    height: 16px;
    object-fit: contain;
`;

const EmojiGlyph = memo(({ shortcode, customEmojis }) => {
    const [failed, setFailed] = useState(false);
    const handleError = useCallback(() => setFailed(true), []);
    const unicode = resolveUnicode(shortcode);
    if (unicode) {
        return <Glyph>{unicode}</Glyph>;
    }
    const name = stripColons(shortcode);
    const custom = (customEmojis || []).find(e => e.name === name || (Array.isArray(e.aliases) && e.aliases.includes(name)));
    if (failed) {
        return <FallbackText>{`:${name}:`}</FallbackText>;
    }
    if (custom) {
        const src = `/chat/emoji-custom/${encodeURIComponent(custom.name)}.${safeEmojiExtension(custom.extension)}`;
        return <CustomEmojiImg src={src} alt={`:${name}:`} onError={handleError} />;
    }
    return (
        <CustomEmojiImg
            src={`/chat/emoji-custom/${encodeURIComponent(name)}.png`}
            alt={`:${name}:`}
            onError={handleError}
        />
    );
});

EmojiGlyph.propTypes = {
    shortcode: PropTypes.string.isRequired,
    customEmojis: PropTypes.array,
};

const ReactionChip = memo(({ emoji, active, tooltip, count, customEmojis, onToggle, canReact }) => {
    const handleClick = useCallback(() => onToggle(emoji), [emoji, onToggle]);
    return (
        <Tooltip title={tooltip} placement="bottom">
            <Chip
                type="button"
                active={active}
                onClick={handleClick}
                disabled={!canReact}
                aria-pressed={active}
            >
                <EmojiGlyph shortcode={emoji} customEmojis={customEmojis} />
                <span>{count}</span>
            </Chip>
        </Tooltip>
    );
});

ReactionChip.propTypes = {
    emoji: PropTypes.string.isRequired,
    active: PropTypes.bool,
    tooltip: PropTypes.string,
    count: PropTypes.number.isRequired,
    customEmojis: PropTypes.array,
    onToggle: PropTypes.func.isRequired,
    canReact: PropTypes.bool,
};

const MessageReactions = ({ reactions, currentUsername, onToggle, customEmojis, canReact = true }) => {
    if (!reactions || reactions.length === 0) return null;
    return (
        <ReactionsRow role="toolbar">
            {reactions.map(r => {
                const active = Array.isArray(r.usernames) && r.usernames.includes(currentUsername);
                const tooltip = ((r.names && r.names.length ? r.names : r.usernames) || []).join(', ');
                return (
                    <ReactionChip
                        key={r.emoji}
                        emoji={r.emoji}
                        active={active}
                        tooltip={tooltip}
                        count={r.count}
                        customEmojis={customEmojis}
                        onToggle={onToggle}
                        canReact={canReact}
                    />
                );
            })}
        </ReactionsRow>
    );
};

MessageReactions.propTypes = {
    reactions: PropTypes.arrayOf(
        PropTypes.shape({
            emoji: PropTypes.string.isRequired,
            usernames: PropTypes.array,
            names: PropTypes.array,
            count: PropTypes.number.isRequired,
        })
    ),
    currentUsername: PropTypes.string,
    onToggle: PropTypes.func.isRequired,
    customEmojis: PropTypes.array,
    canReact: PropTypes.bool,
};

export default memo(MessageReactions);
